import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControlLabel,
    Checkbox,
    Box,
} from '@mui/material';
import { post, put } from 'src/utils/networkUtils';

// ----------------------------------------------------------------------

CompetitionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    competition: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
};

export default function CompetitionDialog({ open, competition, onClose, onSuccess }) {
    const [formData, setFormData] = useState({
        tenantId: 1,
        externalId: '',
        name: '',
        sport: '',
        isInternational: false,
    });
    const [loading, setLoading] = useState(false);

    const isEditMode = Boolean(competition);

    useEffect(() => {
        if (competition) {
            setFormData({
                tenantId: competition.tenantId || 1,
                externalId: competition.externalId || '',
                name: competition.name || '',
                sport: competition.sport || '',
                isInternational: Boolean(competition.isInternational),
            });
        } else {
            setFormData({
                tenantId: 1,
                externalId: '',
                name: '',
                sport: '',
                isInternational: false,
            });
        }
    }, [competition]);

    const handleChange = (field) => (event) => {
        let value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

        // Convert tenantId to number
        if (field === 'tenantId' && value !== '') {
            value = parseInt(value, 10) || 1;
        }

        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (isEditMode) {
                // Edit existing competition
                await put(`/admin/competitions/${competition.competitionId}`, formData);
            } else {
                // Create new competition
                await post(`/admin/competitions`, formData);
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving competition:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const isFormValid = formData.name && formData.name.trim() && formData.externalId && formData.externalId.trim() && formData.tenantId;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {isEditMode ? 'Edit Competition' : 'Create Competition'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                label="Competition Name"
                                value={formData.name}
                                onChange={handleChange('name')}
                                fullWidth
                                required
                                disabled={loading}
                                placeholder="e.g., Premier League"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="External ID"
                                value={formData.externalId}
                                onChange={handleChange('externalId')}
                                fullWidth
                                required
                                disabled={loading}
                                placeholder="e.g., 8"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Sport"
                                value={formData.sport}
                                onChange={handleChange('sport')}
                                fullWidth
                                disabled={loading}
                                placeholder="e.g., Basketball, Hockey, Football"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isInternational}
                                        onChange={handleChange('isInternational')}
                                        disabled={loading}
                                    />
                                }
                                label="International Competition"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !isFormValid}
                >
                    {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
